import { ReactElement, useState } from "react";

import { css } from "@compiled/react";

import Link from "@/components/wpl/link/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { ProductKeyTypes } from "@/libraries/wpl/foundations/src";

export type CardIcon = { normal: ReactElement; hover?: ReactElement };
type CardProps = {
    content: string;
    href: string;
    icon?: CardIcon;
    title: string;
    productKey?: ProductKeyTypes;
};

const cardStyles = css({
    display: "flex",
    flexDirection: "column",
    width: "11.5rem",

    svg: {
        marginBlockEnd: "0.5rem"
    }
});

const CardTitleCardContentStyle = css({
    fontSize: "11.5px",
    lineHeight: "normal",
    color: "#505258",
    margin: 0,
    fontWeight: "400",
    letterSpacing: 0
});

const CardTitleAnchorLinkStyle = css({
    "alignItems": "center",
    "color": "#505258",
    "display": "block",
    "&:visited": {
        color: "inherit"
    },
    "&:hover, &:focus": {
        color: "#0747A6"
    },
    "svg": {
        transition: "transform 0.25s ease-in-out"
    },

    "h6.titleCard-title": {
        "lineHeight": "normal",
        "margin": 0,
        "color": "inherit",
        "&:visited": {
            color: "inherit"
        },
        "&:hover, &:focus": {
            color: "inherit"
        },
        "fontSize": "14px",
        "letterSpacing": "0.03em"
    }
});

const Icon = ({ normal, hover }: CardIcon) => {
    const [isHovered, setIsHovered] = useState(false);

    const icon = isHovered && typeof hover !== "undefined" ? hover : normal;

    return (
        <div
            css={css({
                display: "block",
                alignItems: "end",
                height: "36px",
                width: "36px"
            })}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {icon}
        </div>
    );
};

export const TitleCard = ({
    title,
    icon,
    content,
    href,
    productKey = "home"
}: CardProps) => {
    const localizedHref = useLocalizedHref(href);

    return (
        <div css={cardStyles}>
            <a
                className="title-card-link"
                href={localizedHref}
                target={""}
                rel={""}
                css={CardTitleAnchorLinkStyle}
                aria-label={title}
                data-event="clicked"
                data-event-component="linkButton"
                data-event-container="globalNavSubnav"
                data-product-key={productKey}
            >
                {!!icon && <Icon {...icon} />}
                <h6 className="titleCard-title">{title}</h6>
                <div
                    className="titleCard-content"
                    css={CardTitleCardContentStyle}
                >
                    {content}
                </div>
            </a>
        </div>
    );
};

type LinkCardProps = {
    href: string;
    text: string;
    productKey?: ProductKeyTypes;
};

const linkCardStyles = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid #DDDEE1",
    borderRadius: "1rem",

    a: {
        "color": "#101214",
        "fontSize": "0.875rem",

        "svg > path": {
            fill: "#101214"
        }
    }
});

export const LinkCard = ({
    href,
    text,
    productKey = "home"
}: LinkCardProps) => {
    const localizedHref = useLocalizedHref(href);

    return (
        <div css={linkCardStyles}>
            <Link
                href={localizedHref}
                text={text}
                ariaLabel={text}
                type="basic"
                dataEventProps={{
                    "data-event": "clicked",
                    "data-event-component": "linkButton",
                    "data-event-container": "globalNavSubnav",
                    "data-product-key": productKey
                }}
            />
        </div>
    );
};
